import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Search, RefreshCw, Eye, X, Copy, FileDown, GraduationCap, Save } from "lucide-react";
import { jsPDF } from "jspdf";
import { api } from "@/lib/api";

const STATUSES = ["New", "Contacted", "Scheduling", "Scheduled", "Active", "Completed", "Inactive"];
const TOPICS = ["Computer Skills", "English", "Spanish", "Business Support", "Other"];
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("es", { year: "numeric", month: "short", day: "numeric" }); } catch { return iso; } };
const NA = "[INFORMACIÓN NO PROPORCIONADA]";
const empty = (v) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
const val = (v) => empty(v) ? NA : (Array.isArray(v) ? v.join(", ") : String(v));

/* ---------- learning plan generator (client facts + clearly-marked suggestions) ---------- */
function buildLearningPlan(r) {
  const skills = !empty(r.computer_skills) ? r.computer_skills : (!empty(r.skills_to_practice) ? [r.skills_to_practice] : []);
  const isNew = (r.experience_level || "").startsWith("I am completely new");
  const priorities = [];
  if (isNew) priorities.push(`Comenzar con los fundamentos de ${r.topic} antes de avanzar a temas específicos.`);
  if (!empty(r.learning_goal)) priorities.push(`Priorizar las actividades que apoyen directamente la meta del estudiante: "${r.learning_goal}".`);
  if (skills.length) priorities.push(`Practicar las habilidades específicas solicitadas: ${skills.slice(0, 4).join("; ")}${skills.length > 4 ? "; …" : ""}.`);
  if (r.topic === "Computer Skills" && !empty(r.devices)) priorities.push(`Practicar en el dispositivo del estudiante (${r.devices.join(", ")}) siempre que sea posible.`);
  if (!priorities.length) priorities.push(NA);
  return `PLAN DE APRENDIZAJE / LEARNING PLAN
${"=".repeat(46)}

ESTUDIANTE / STUDENT
  ${val(r.full_name)}

TEMA / TOPIC
  ${val(r.topic)}${r.topic === "Other" && r.topic_other ? ` — ${r.topic_other}` : ""}

NIVEL ACTUAL / CURRENT LEVEL
  ${val(r.experience_level)}

META PRINCIPAL / PRIMARY GOAL
  ${val(r.desired_outcome)}
  Detalle: ${val(r.learning_goal)}
  Motivación: ${val(r.learning_reason)}

HABILIDADES ESPECÍFICAS / SPECIFIC SKILLS
${skills.length ? skills.map((s) => `  - ${s}`).join("\n") : `  ${val(r.skills_to_practice)}`}

PRIORIDADES SUGERIDAS / SUGGESTED LEARNING PRIORITIES
  (Recomendaciones basadas en las metas declaradas por el estudiante — no son hechos sobre el estudiante.)
${priorities.map((p) => `  - ${p}`).join("\n")}

PRIMERA SESIÓN SUGERIDA / SUGGESTED FIRST SESSION
  (Recomendación — ajustar según la evaluación del instructor.)
  Objetivo sugerido: Conocer al estudiante, evaluar su nivel actual y dar una primera práctica exitosa relacionada con su meta.
  Habilidades a evaluar: ${skills.length ? skills.slice(0, 3).join("; ") : val(r.skills_to_practice)}
  Habilidades a introducir o practicar: ${skills.length ? skills[0] : NA}
  Actividades sugeridas: conversación diagnóstica, demostración guiada, práctica supervisada paso a paso.
  Tarea de práctica posible: practicar 15 minutos entre sesiones la habilidad trabajada en la primera sesión.

IDEAS PARA PRÓXIMAS SESIONES / FUTURE SESSION IDEAS
  (Recomendación basada en las metas declaradas.)
  - Avanzar progresivamente sobre las habilidades solicitadas, una por sesión.
  - Revisar la tarea de práctica al inicio de cada sesión.
  - Medir el progreso contra la meta principal: "${val(r.desired_outcome)}"

NOTAS DEL ESTUDIANTE / STUDENT NOTES
  Disponibilidad: ${val(r.days)} · ${val(r.times)}
  Modalidad: ${val(r.session_format)} · Inicio deseado: ${val(r.start_timing)}
  Notas de horario: ${val(r.schedule_notes)}
  Para preparar: ${val(r.prepare_notes)}`;
}

const Sec = ({ t, es, children }) => (
  <div className="mb-4 bg-offwhite rounded-2xl p-4 border border-navy/5">
    <h4 className="font-heading font-bold text-navy mb-2">{t} <span className="text-ink/40 text-sm font-semibold italic">/ {es}</span></h4>
    {children}
  </div>
);
const Row = ({ l, v }) => <div className="mb-1.5 text-sm"><span className="font-semibold text-navy">{l}: </span><span className="text-ink/70 whitespace-pre-wrap">{val(v)}</span></div>;

function DetailModal({ req, onClose, onPatch }) {
  const [notes, setNotes] = useState(req.admin_notes || "");
  const [plan, setPlan] = useState(req.learning_plan || "");
  const [planOpen, setPlanOpen] = useState(!!req.learning_plan);

  const generate = () => { setPlan(buildLearningPlan(req)); setPlanOpen(true); };
  const copyPlan = async () => { try { await navigator.clipboard.writeText(plan); alert("Plan copiado al portapapeles."); } catch { alert("No se pudo copiar."); } };
  const pdfPlan = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 40; const width = doc.internal.pageSize.getWidth() - margin * 2;
    let y = margin;
    doc.setFontSize(10); doc.setFont("helvetica", "normal"); doc.setTextColor("#172033");
    doc.splitTextToSize(plan, width).forEach((ln) => {
      if (y > doc.internal.pageSize.getHeight() - margin) { doc.addPage(); y = margin; }
      doc.text(ln, margin, y); y += 14;
    });
    const safe = (req.full_name || "estudiante").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
    doc.save(`plan_${safe}.pdf`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose} data-testid="ps-detail-modal">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-navy text-white px-6 py-4 flex items-center justify-between rounded-t-3xl z-10">
          <div><h3 className="font-heading font-extrabold text-lg">{req.full_name}</h3><p className="text-white/60 text-sm">{req.topic}{req.topic === "Other" && req.topic_other ? ` — ${req.topic_other}` : ""} · {fmt(req.created_at)}</p></div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="ps-detail-close"><X size={22} /></button>
        </div>
        <div className="p-6">
          <div className="flex flex-wrap gap-3 mb-5">
            <select value={req.status} onChange={(e) => onPatch(req.id, { status: e.target.value })} data-testid="ps-detail-status" className="bg-white border border-navy/15 rounded-full px-4 py-2 text-sm font-semibold">{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
            <button onClick={planOpen ? () => setPlanOpen(false) : generate} data-testid="ps-create-plan" className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${planOpen ? "bg-navy text-white" : "bg-gold text-navy"}`}><GraduationCap size={15} /> {planOpen ? "Ocultar Plan" : "Crear Plan de Aprendizaje"}</button>
          </div>

          {planOpen && (
            <div className="mb-6 border border-brand/30 rounded-2xl p-4 bg-aqua/20" data-testid="ps-plan-panel">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-sm font-bold text-navy">Plan de Aprendizaje (editable)</span>
                <button onClick={() => onPatch(req.id, { learning_plan: plan })} data-testid="ps-save-plan" className="inline-flex items-center gap-1.5 bg-brand text-white rounded-full px-3 py-1.5 text-xs font-semibold"><Save size={13} /> Guardar plan</button>
                <button onClick={copyPlan} data-testid="ps-copy-plan" className="inline-flex items-center gap-1.5 bg-navy text-white rounded-full px-3 py-1.5 text-xs font-semibold"><Copy size={13} /> Copiar Plan</button>
                <button onClick={pdfPlan} data-testid="ps-pdf-plan" className="inline-flex items-center gap-1.5 bg-white border border-navy/15 rounded-full px-3 py-1.5 text-xs font-semibold"><FileDown size={13} /> Imprimir / Guardar como PDF</button>
              </div>
              <textarea value={plan} onChange={(e) => setPlan(e.target.value)} rows={18} className="w-full bg-white border border-navy/15 rounded-xl px-3 py-2 text-xs font-mono whitespace-pre" data-testid="ps-plan-text" />
            </div>
          )}

          <Sec t="Contact Information" es="Información de Contacto">
            <Row l="Name / Nombre" v={req.full_name} /><Row l="Phone / Teléfono" v={req.phone} /><Row l="Email" v={req.email} />
            <Row l="Preferred language / Idioma" v={req.preferred_language === "Other" ? req.preferred_language_other : req.preferred_language} />
            <Row l="Referral / Referido" v={req.referral === "Other / Otro" ? req.referral_other : req.referral} />
          </Sec>
          <Sec t="Learning Goals" es="Metas de Aprendizaje">
            <Row l="Topic / Tema" v={req.topic === "Other" ? req.topic_other : req.topic} />
            <Row l="Learn or improve / Aprender o mejorar" v={req.learning_goal} />
            <Row l="Desired outcome / Resultado deseado" v={req.desired_outcome} />
            <Row l="Why now / Por qué ahora" v={req.learning_reason} />
          </Sec>
          <Sec t="Current Skill Level" es="Nivel Actual"><Row l="Experience / Experiencia" v={req.experience_level} /></Sec>
          <Sec t="Skills to Practice" es="Habilidades para Practicar">
            <Row l="Skills / Habilidades" v={req.skills_to_practice} />
            {req.topic === "Computer Skills" && <>
              <Row l="Computer skills / Computación" v={req.computer_skills} />
              {req.computer_skills_other && <Row l="Other / Otro" v={req.computer_skills_other} />}
              <Row l="Devices / Dispositivos" v={req.devices} />
              <Row l="Brings device / Trae dispositivo" v={req.bring_device} />
            </>}
          </Sec>
          <Sec t="Scheduling" es="Horario">
            <Row l="Start / Inicio" v={req.start_timing} /><Row l="Format / Modalidad" v={req.session_format} />
            <Row l="Days / Días" v={req.days} /><Row l="Times / Horarios" v={req.times} />
            <Row l="Specific times / Horarios específicos" v={req.schedule_notes} />
          </Sec>
          <Sec t="Additional Information" es="Información Adicional">
            <Row l="Prepare notes / Notas" v={req.prepare_notes} />
            <Row l="Agreement / Acuerdo" v={req.agreement ? "Accepted / Aceptado" : "No"} />
            <Row l="Submitted / Enviado" v={fmt(req.created_at)} />
          </Sec>

          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Private Admin Notes / Notas Administrativas Privadas</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => onPatch(req.id, { admin_notes: notes })} rows={3} className="w-full bg-white border border-navy/15 rounded-xl px-3 py-2 text-sm" data-testid="ps-admin-notes" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PrivateSessionsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [fTopic, setFTopic] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => { setLoading(true); try { const { data } = await api.get("/private-sessions"); setItems(data); } catch (e) { /* ignore */ } setLoading(false); }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, changes) => {
    setItems((p) => p.map((r) => r.id === id ? { ...r, ...changes } : r));
    setOpen((o) => (o && o.id === id ? { ...o, ...changes } : o));
    try { await api.patch(`/private-sessions/${id}`, changes); } catch (e) { load(); }
  };

  const filtered = useMemo(() => items.filter((r) => {
    const hay = `${r.full_name} ${r.email} ${r.phone} ${r.topic}`.toLowerCase();
    if (q && !hay.includes(q.toLowerCase())) return false;
    if (fTopic !== "all" && r.topic !== fTopic) return false;
    if (fStatus !== "all" && r.status !== fStatus) return false;
    return true;
  }), [items, q, fTopic, fStatus]);

  const cards = useMemo(() => [
    ["New / Nuevas", items.filter((r) => r.status === "New").length],
    ["Contacted / Contactados", items.filter((r) => r.status === "Contacted").length],
    ["Scheduled / Programados", items.filter((r) => r.status === "Scheduled").length],
    ["Active Students / Activos", items.filter((r) => r.status === "Active").length],
    ["Completed / Completados", items.filter((r) => r.status === "Completed").length],
  ], [items]);

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="ps-summary">
        {cards.map(([label, v]) => (
          <div key={label} className="bg-white rounded-2xl border border-navy/5 p-4 text-center">
            <div className="text-2xl font-extrabold text-brand">{v}</div>
            <div className="text-[10px] uppercase tracking-wide text-ink/50 mt-1 leading-tight">{label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
          <input data-testid="ps-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone / Buscar" className="w-full bg-white border border-navy/10 rounded-full pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand" />
        </div>
        <select value={fTopic} onChange={(e) => setFTopic(e.target.value)} data-testid="ps-filter-topic" className="bg-white border border-navy/10 rounded-full px-4 py-2.5 text-sm"><option value="all">All Topics / Temas</option>{TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="ps-filter-status" className="bg-white border border-navy/10 rounded-full px-4 py-2.5 text-sm"><option value="all">All Statuses / Estados</option>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <button onClick={load} className="inline-flex items-center gap-2 bg-white border border-navy/10 rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-aqua"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /></button>
      </div>

      {filtered.length === 0 ? <div className="text-center py-20 bg-white rounded-3xl border text-ink/50">No private session requests yet. / No hay solicitudes todavía.</div> :
        <div className="bg-white rounded-3xl border border-navy/5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ps-table">
              <thead className="bg-offwhite text-navy"><tr>{["Name / Nombre", "Topic / Tema", "Language / Idioma", "Format / Modalidad", "Requested Start / Inicio", "Date / Fecha", "Status / Estado", ""].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-navy/5 hover:bg-aqua/20" data-testid={`ps-row-${r.id}`}>
                    <td className="px-4 py-3 font-semibold text-navy whitespace-nowrap">{r.full_name}</td>
                    <td className="px-4 py-3 text-ink/70 whitespace-nowrap">{r.topic}</td>
                    <td className="px-4 py-3 text-ink/70">{r.preferred_language}</td>
                    <td className="px-4 py-3 text-ink/70 whitespace-nowrap">{(r.session_format || "").split(" / ")[0]}</td>
                    <td className="px-4 py-3 text-ink/70 whitespace-nowrap">{(r.start_timing || "").split(" / ")[0]}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-ink/60">{fmt(r.created_at)}</td>
                    <td className="px-4 py-3"><span className="text-xs bg-aqua text-navy rounded-full px-2.5 py-1 font-semibold whitespace-nowrap">{r.status}</span></td>
                    <td className="px-4 py-3"><button onClick={() => setOpen(r)} data-testid={`ps-view-${r.id}`} className="inline-flex items-center gap-1 text-brand font-semibold whitespace-nowrap"><Eye size={15} /> View / Ver</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>}

      {open && <DetailModal req={open} onClose={() => setOpen(null)} onPatch={patch} />}
    </div>
  );
}
